import React, { useState, useRef, useContext } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Paper,
  Icon,
  Checkbox,
  TextField,
  TableSortLabel,
  IconButton,
  Menu,
  Button,
  MenuItem,
  CircularProgress,
  Select,
} from '@material-ui/core';
import { AuthContext } from '../../context/authContext';
import './DataTable.css';

export interface ColumnDefinition {
  id: string;
  label: string;
  type?: 'text' | 'checkbox' | 'input';
  disabled?: boolean;
  onInlineChange?: (event: any, row: any, rowIndex: number) => void;
  onInlineBlur?: (event: any, row: any, rowIndex: number) => void;
  render?: (row: any, index: number) => React.ReactNode;
}

export interface ActionDefinition {
  id: string;
  icon: string | React.ReactNode;
  tooltip?: string;
  access?: string;
  onClick: (actionId: string, row: any, rowIndex: number) => void;
}

export interface DataTableProps {
  title?: string;
  columns: ColumnDefinition[];
  data: any[];
  actions?: ActionDefinition[];
  loading?: boolean;
  headerActions?: React.ReactNode;

  // Pagination Configuration
  page: number; // 0-indexed
  limit: number;
  hasMore: boolean;
  onPageChange: (newPage: number) => void;
  onLimitChange: (newLimit: number) => void;

  // Sorting
  orderBy: string;
  order: 'asc' | 'desc';
  onSort: (columnId: string, newOrder: 'asc' | 'desc') => void;

  // Filtering (per column)
  filters: Record<string, string>;
  onFilterChange: (filters: Record<string, string>) => void;

  // Row Interaction
  onRowClick?: (row: any, index: number) => void;
}

export const DataTable: React.FC<DataTableProps> = ({
  title,
  columns,
  data,
  actions = [],
  loading = false,
  headerActions,
  page,
  limit,
  hasMore,
  onPageChange,
  onLimitChange,
  orderBy,
  order,
  onSort,
  filters,
  onFilterChange,
  onRowClick,
}) => {
  const authContext = useContext(AuthContext) as any;
  const hasAccess = authContext && authContext.hasAccess ? authContext.hasAccess : () => true;

  // Local state for UI popovers
  const [activeSearchCol, setActiveSearchCol] = useState<string | null>(null);
  const [searchAnchorEl, setSearchAnchorEl] = useState<null | HTMLElement>(null);
  const [localFilterValues, setLocalFilterValues] = useState<Record<string, string>>(filters);
  const searchInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [actionRow, setActionRow] = useState<{ row: any, index: number } | null>(null);

  // Sorting Handler
  const handleSortClick = (columnId: string) => {
    const isAsc = orderBy === columnId && order === 'asc';
    onSort(columnId, isAsc ? 'desc' : 'asc');
  };

  // Search/Filter Handlers
  const handleOpenSearch = (event: React.MouseEvent<HTMLElement>, columnId: string) => {
    setSearchAnchorEl(event.currentTarget);
    setActiveSearchCol(columnId);
    setTimeout(() => {
      if (searchInputRefs.current[columnId]) {
        searchInputRefs.current[columnId]!.focus();
      }
    }, 100);
  };

  const handleCloseSearch = () => {
    setSearchAnchorEl(null);
    setActiveSearchCol(null);
  };

  const handleLocalFilterChange = (columnId: string, value: string) => {
    setLocalFilterValues((prev) => ({ ...prev, [columnId]: value }));
  };

  const applySearch = (columnId: string) => {
    const newFilters = { ...filters, [columnId]: localFilterValues[columnId] || '' };
    if (!newFilters[columnId]) delete newFilters[columnId];
    onFilterChange(newFilters);
    handleCloseSearch();
  };

  const resetSearch = (columnId: string) => {
    setLocalFilterValues((prev) => ({ ...prev, [columnId]: '' }));
    const newFilters = { ...filters };
    delete newFilters[columnId];
    onFilterChange(newFilters);
    handleCloseSearch();
  };

  // Actions Handlers
  const handleOpenActions = (event: React.MouseEvent<HTMLElement>, row: any, index: number) => {
    event.stopPropagation();
    setActionAnchorEl(event.currentTarget);
    setActionRow({ row, index });
  };

  const handleCloseActions = () => {
    setActionAnchorEl(null);
    setActionRow(null);
  };

  return (
    <div className="datatable-wrapper">
      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        {(title || headerActions) && (
          <div className="datatable-header">
            <div>{title && <h1 className="text-xl font-semibold text-gray-800">{title}</h1>}</div>
            <div>{headerActions}</div>
          </div>
        )}

        <div className="datatable-box">
          {loading && (
            <div className="datatable-overlay">
              <CircularProgress size={60} />
            </div>
          )}

          <TableContainer>
            <Table aria-label="data-table" size="small">
              <TableHead>
                <TableRow>
                  {actions.length > 0 && <TableCell className="datatable-actions-cell">Acciones</TableCell>}
                  {columns.map((col) => (
                    <TableCell key={col.id}>
                      <div className="datatable-th-content">
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'asc'}
                          onClick={() => handleSortClick(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                        <div>
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenSearch(e, col.id)}
                            aria-haspopup="true"
                          >
                            <Icon style={{ color: filters[col.id] ? '#f50057' : 'inherit' }}>search</Icon>
                          </IconButton>
                        </div>
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length + (actions.length > 0 ? 1 : 0)} style={{ textAlign: 'center' }}>
                      No hay registros
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row, i) => (
                    <TableRow
                      key={row.id || i}
                      hover
                      className={onRowClick ? 'datatable-row-clickable' : ''}
                      onClick={() => onRowClick && onRowClick(row, i)}
                    >
                      {actions.length > 0 && (
                        <TableCell className="datatable-actions-cell">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenActions(e, row, i)}
                          >
                            <Icon color="primary">more_vert</Icon>
                          </IconButton>
                        </TableCell>
                      )}

                      {columns.map((col) => {
                        return (
                          <TableCell key={col.id} onClick={(e) => {
                            if (col.type === 'checkbox' || col.type === 'input') {
                              e.stopPropagation();
                            }
                          }}>
                            {col.render ? (
                              col.render(row, i)
                            ) : col.type === 'checkbox' ? (
                              <Checkbox
                                disabled={col.disabled}
                                checked={!!row[col.id]}
                                onChange={(e) => col.onInlineChange && col.onInlineChange(e, row, i)}
                                onBlur={(e) => col.onInlineBlur && col.onInlineBlur(e, row, i)}
                              />
                            ) : col.type === 'input' ? (
                              <TextField
                                disabled={col.disabled}
                                variant="outlined"
                                size="small"
                                value={row[col.id] || ''}
                                onChange={(e) => col.onInlineChange && col.onInlineChange(e, row, i)}
                                onBlur={(e) => col.onInlineBlur && col.onInlineBlur(e, row, i)}
                              />
                            ) : (
                              row[col.id]
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Custom Simplified Pagination without Total */}
          <div className="datatable-pagination">
            <div className="datatable-pagination-controls">
              <span className="pagination-text">Filas por p&#225;gina:</span>
              <Select
                native
                value={limit}
                onChange={(e) => onLimitChange(Number(e.target.value))}
                className="pagination-select"
                disableUnderline
              >
                {[5, 10, 15, 25, 30, 50].map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </Select>

              <span className="pagination-text" style={{ marginLeft: '16px', marginRight: '16px' }}>
                P&#225;gina {page + 1}
              </span>

              <IconButton
                size="small"
                disabled={page === 0}
                onClick={() => onPageChange(page - 1)}
              >
                <Icon>chevron_left</Icon>
              </IconButton>

              <IconButton
                size="small"
                disabled={!hasMore}
                onClick={() => onPageChange(page + 1)}
              >
                <Icon>chevron_right</Icon>
              </IconButton>
            </div>
          </div>
        </div>

        {/* Dynamic Column Search Menu */}
        <Menu
          anchorEl={searchAnchorEl}
          open={Boolean(searchAnchorEl)}
          onClose={handleCloseSearch}
          elevation={3}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {activeSearchCol && (
            <div className="datatable-search-menu">
              <TextField
                inputRef={(el) => searchInputRefs.current[activeSearchCol] = el}
                size="small"
                variant="outlined"
                placeholder="Buscar..."
                value={localFilterValues[activeSearchCol] || ''}
                onChange={(e) => handleLocalFilterChange(activeSearchCol, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applySearch(activeSearchCol); }}
                style={{ marginBottom: '8px' }}
              />
              <div className="datatable-search-actions">
                <Button size="small" variant="outlined" color="primary" onClick={() => applySearch(activeSearchCol)}>
                  Buscar
                </Button>
                <Button size="small" variant="outlined" color="secondary" onClick={() => resetSearch(activeSearchCol)}>
                  Reset
                </Button>
              </div>
            </div>
          )}
        </Menu>

        {/* Row Actions Menu */}
        <Menu
          anchorEl={actionAnchorEl}
          open={Boolean(actionAnchorEl)}
          onClose={handleCloseActions}
        >
          {actions.map((action, idx) => {
            if (action.access && !hasAccess(action.access)) return null;
            return (
              <MenuItem
                key={action.id || idx}
                onClick={() => {
                  handleCloseActions();
                  if (actionRow) {
                    action.onClick(action.id, actionRow.row, actionRow.index);
                  }
                }}
              >
                <Icon color="primary" style={{ marginRight: '8px' }}>{action.icon}</Icon>
                {action.tooltip || action.id}
              </MenuItem>
            );
          })}
        </Menu>
      </div>
    </div>
  );
};
